/**
 * SafeCurves legend helpers — wired into `safecurvesConfig.parseLegendLayers`.
 *
 * `parseLegendLayers` converts the SafeCurves `ISafeCurvesSystemLayers`
 * envelope into the `MapLayer[]` array consumed by `legendStore`.
 */

import type { ILayerSource } from '#lib/types.js';
import {
	EIdentify,
	EPrioritize,
	EInterventions,
	ESafeCurvesModule,
	type ISafeCurvesSystemLayers
} from './interfaces';
import type { ICrashesSystemLayers } from '#lib/apps/safesystem/context/interfaces.js';
import { mapPriorityDescription } from './prioritize';
import type { MapLayer } from '#lib/stores/legendStore.svelte.js';
import { SIGN_LEVELS, signSpeed } from './legends/signImages';
import {
	buildLegendLayers,
	parseMatchPaint,
	parseSinglePaint,
	type Parsers
} from '../legendHelpers';

function parseImageItems(
	feature: ILayerSource,
	filterAttr: string,
	items: readonly (readonly [string, string])[]
) {
	return {
		filterAttr,
		defaultEnabled: false,
		legendItems: items.map(([label, image]) => ({
			legendItemId: `${feature.id}:${label.replace(' ', '_')}`,
			filterValue: label,
			label,
			image,
			enabled: false
		}))
	};
}

const parseSigns = (f: ILayerSource) =>
	parseImageItems(f, 'advisory_sign_recommendation', SIGN_LEVELS);

export const pffaLabelFor = (raw: string) => {
	if (raw === 'None') return 'Adjacent lane (TP to TP)';
	if (raw === 'Maximum') return 'Aggressive deceleration';
	return `${raw} deceleration`;
};

const parsePrioritize = (f: ILayerSource) =>
	parseMatchPaint(f, 'line-color', {
		defaultEnabled: true,
		labelFor: (raw) => mapPriorityDescription[raw],
		idSuffixFor: (raw) => mapPriorityDescription[raw].replace(' ', '_')
	});

const parsers: Parsers = {
	[EIdentify.Identify]: (f) => parseMatchPaint(f, 'line-color'),
	[EPrioritize.Prioritize]: parsePrioritize,
	// Simplified corridors mirror Prioritize's items + filterAttr; no legend UI of their own.
	[EPrioritize.PrioritizeDisplayOnly]: parsePrioritize,
	[EInterventions.CurveAdvisorySigns]: parseSigns,
	// Halo mirrors the signs' items + filterAttr so syncVisibleLayers applies the same filter; it has no legend UI of its own.
	[EInterventions.CurveAdvisorySignsHalo]: parseSigns,
	[EInterventions.CurveAdvisorySpeeds]: (f) =>
		parseImageItems(
			f,
			'advisory_sign_recommendation',
			SIGN_LEVELS.map(([label]) => [label, signSpeed])
		),
	[EInterventions.PavementFrictionFocusAreas]: (f) =>
		parseMatchPaint(f, 'line-color', {
			labelFor: pffaLabelFor,
			idSuffixFor: (raw) => `hfst:${raw.replace(' ', '_')}`
		}),
	[EInterventions.SpeedLimitReview]: (f) =>
		parseMatchPaint(f, 'line-color', {
			idSuffixFor: (raw) => `speedlimitreview:${raw.replace(' ', '_')}`
		}),
	[EInterventions.PassingSightDistance]: (f) =>
		parseSinglePaint(f, 'line-color', 'passingsightdistance', 'No passing lines (visibility)'),
	[EInterventions.StoppingSightDistance]: (f) =>
		parseSinglePaint(f, 'line-color', 'stoppingsightdistance', 'Insufficient SSD - light vehicles'),
	[EInterventions.SSDObstructions]: (f) =>
		parseSinglePaint(f, 'fill-color', 'ssdobstructions', 'Obstruction'),
	[EInterventions.ClearZone]: (f) =>
		parseMatchPaint(f, 'line-color', {
			idSuffixFor: (raw) => `clearzone:${raw.replace(' ', '_')}`
		})
};

export function parseLegendLayers(
	payload: ISafeCurvesSystemLayers & ICrashesSystemLayers
): MapLayer[] {
	// SafeCurves defaults to showing Prioritize; when that module's data is
	// absent, fall back to enabling every available SafeCurves layer so the map
	// isn't blank. Crash overlays keep their own per-parser defaults.
	const prioritizeAvailable = Boolean(
		payload[ESafeCurvesModule.Prioritize]?.[EPrioritize.Prioritize]
	);
	const modules: string[] = Object.values(ESafeCurvesModule);

	return buildLegendLayers(payload, modules, parsers, (module, defaultEnabled, layerId) => {
		const own = modules.includes(module);
		// Crash and simplified-Prioritize tiles lack the mapDataFilter attributes, so they opt out of that filter.
		return {
			enabled: !prioritizeAvailable && own ? true : defaultEnabled,
			excludeFromMapDataFilter: !own || layerId === EPrioritize.PrioritizeDisplayOnly
		};
	});
}
